// =============================================================================
// MeuDriver — useAdminUsers (Batch J3)
// =============================================================================
// Hooks para gerenciamento de usuários pelo admin.
// Todas as operações passam por RPCs SECURITY DEFINER que validam role = admin.
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export type UserStatus = 'active' | 'banned' | 'suspended'
export type UserRole   = 'student' | 'instructor' | 'admin'

export interface AdminUserListItem {
  id: string
  full_name: string | null
  avatar_url: string | null
  role: UserRole
  status: UserStatus
  city: string | null
  state: string | null
  created_at: string
  email: string | null
  booking_count_student: number
  booking_count_instructor: number
}

export interface AdminUserListResult {
  users: AdminUserListItem[]
  total: number
  page: number
  page_size: number
}

export interface AdminUserDetail {
  profile: {
    id: string
    full_name: string | null
    avatar_url: string | null
    role: UserRole
    status: UserStatus
    phone: string | null
    city: string | null
    state: string | null
    created_at: string
    is_premium: boolean
    is_pro_instructor: boolean
    email: string | null
  }
  bookings: Array<{
    id: string
    status: string
    scheduled_at: string
    total_price: number | null
    created_at: string
  }>
  stats: {
    total_bookings_student: number
    total_bookings_instructor: number
    avg_rating: number | null
    total_revenue: number
  }
}

export interface AdminUsersListParams {
  page: number
  search: string
  role: string
  status: string
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const adminUserKeys = {
  all: ['admin', 'users'] as const,
  list: (params: AdminUsersListParams) => [...adminUserKeys.all, 'list', params] as const,
  detail: (userId: string) => [...adminUserKeys.all, 'detail', userId] as const,
}

// ---------------------------------------------------------------------------
// Hooks públicos
// ---------------------------------------------------------------------------

/**
 * Lista paginada de usuários com filtros de busca, role e status.
 */
export function useAdminUsersList(params: AdminUsersListParams) {
  return useQuery<AdminUserListResult, Error>({
    queryKey: adminUserKeys.list(params),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_admin_user_list', {
        p_search:    params.search  || null,
        p_role:      params.role    || null,
        p_status:    params.status  || null,
        p_page:      params.page,
        p_page_size: 20,
      })

      if (error) throw new Error(error.message)

      return data as unknown as AdminUserListResult
    },
    staleTime: 1000 * 30,
    placeholderData: (prev) => prev,
  })
}

/**
 * Perfil completo de um usuário com bookings e métricas.
 */
export function useAdminUserDetail(userId: string) {
  return useQuery<AdminUserDetail, Error>({
    queryKey: adminUserKeys.detail(userId),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_admin_user_detail', {
        p_user_id: userId,
      })

      if (error) throw new Error(error.message)

      return data as unknown as AdminUserDetail
    },
    enabled: Boolean(userId),
    staleTime: 1000 * 60,
  })
}

/**
 * Bane um usuário (status → 'banned').
 */
export function useAdminBanUser() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: async (userId: string) => {
      const { error } = await supabase.rpc('admin_set_user_status', {
        p_user_id: userId,
        p_status:  'banned',
      })
      if (error) throw new Error(error.message)
    },
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all })
      queryClient.invalidateQueries({ queryKey: adminUserKeys.detail(userId) })
    },
  })
}

/**
 * Desbane um usuário (status → 'active').
 */
export function useAdminUnbanUser() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: async (userId: string) => {
      const { error } = await supabase.rpc('admin_set_user_status', {
        p_user_id: userId,
        p_status:  'active',
      })
      if (error) throw new Error(error.message)
    },
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all })
      queryClient.invalidateQueries({ queryKey: adminUserKeys.detail(userId) })
    },
  })
}

/**
 * Muda a role de um usuário.
 */
export function useAdminChangeRole() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, { userId: string; role: UserRole }>({
    mutationFn: async ({ userId, role }) => {
      const { error } = await supabase.rpc('admin_set_user_role', {
        p_user_id: userId,
        p_role:    role,
      })
      if (error) throw new Error(error.message)
    },
    onSuccess: (_data, { userId }) => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all })
      queryClient.invalidateQueries({ queryKey: adminUserKeys.detail(userId) })
    },
  })
}
